import React, { useMemo, useState } from "react";
import { renderSlotted } from "./renderSlotted";

// =============================================================================
// VoiceDnaPreview — the distilled voice profile, rebuilt Apple-style.
//
// Design language: lean, professional, BonusClub. White surfaces, hairline
// separators, a single brand-blue accent, generous whitespace, quiet large
// numbers. No colorful card soup. Content is organised as grouped "spec
// sheets" (label/value rows) the way macOS System Settings reads, with a
// minimal segmented section switcher.
//
// This component is rendered inside its OWN dedicated view in CloneEditor
// (separate from the Setup / Training area), so it owns the full stage.
// =============================================================================

const useAgeLabel = (ts) =>
  useMemo(() => {
    if (!ts) return null;
    const minutes = Math.floor((Date.now() - ts) / 60000);
    if (minutes < 1) return "just now";
    if (minutes < 60) return `${minutes}m ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h ago`;
    return `${Math.floor(hours / 24)}d ago`;
  }, [ts]);

const confidenceMeta = (score) => {
  if (score >= 80) return { label: "Strong" };
  if (score >= 60) return { label: "Solid" };
  if (score >= 40) return { label: "Forming" };
  return { label: "Sparse" };
};

// Phrase-bank categories — mirrors functions/cloneEngine/phraseCategories.js.
const PHRASE_CATEGORY_META = [
  { id: "greeting",           label: "Greeting" },
  { id: "trigger",            label: "Trigger" },
  { id: "relevance",          label: "Relevance" },
  { id: "sender_positioning", label: "Sender Positioning" },
  { id: "audience_fit",       label: "Audience Fit" },
  { id: "problem",            label: "Problem" },
  { id: "insight",            label: "Insight" },
  { id: "solution",           label: "Solution" },
  { id: "proof",              label: "Proof" },
  { id: "differentiation",    label: "Differentiation" },
  { id: "risk_reversal",      label: "Risk Reversal" },
  { id: "cta",                label: "Call to Action" },
  { id: "signature",          label: "Signature" },
  { id: "other",              label: "Other" },
];

// Psychology-trigger metadata — mirrors functions/cloneEngine/psychTriggers.js.
const PSYCH_TRIGGER_META = [
  { id: "language_mirroring",   label: "Language Mirroring",   blurb: "Speaks in the recipient's own world & words." },
  { id: "industry_metaphor",    label: "Industry Metaphor",    blurb: "A subtle metaphor from the recipient's field." },
  { id: "insider_vocabulary",   label: "Insider Vocabulary",   blurb: "Precise domain terms that signal real familiarity." },
  { id: "hypothesis_not_claim", label: "Hypothesis, not Claim", blurb: "Careful hypotheses instead of hard claims." },
  { id: "specific_observation", label: "Specific Observation", blurb: "Concrete noticing, never generic praise." },
];

const LENGTH_CLASS_LABEL = { 1: "short", 2: "natural", 3: "long", 4: "very long" };
const prettifySub = (sub) => String(sub || "").replace(/_/g, " ").trim();

const wordOf = (x) => (typeof x === "string" ? x : x?.word || x?.phrase || "");

const hasCharacterData = (c) => {
  if (!c || typeof c !== "object") return false;
  if (c.greeting?.word || c.signOff?.word) return true;
  if (c.catchphrases?.length || c.fillerHabits?.length) return true;
  if (c.characterSummary) return true;
  const p = c.punctuationHabits || {};
  return !!(p.capitalization || p.dashStyle || p.parentheses || p.ellipsis);
};

const hasPhraseBankData = (bank) => {
  if (!bank || typeof bank !== "object") return false;
  return PHRASE_CATEGORY_META.some((c) => Array.isArray(bank[c.id]) && bank[c.id].length > 0);
};

const phraseBankTotal = (bank) => {
  if (!bank || typeof bank !== "object") return 0;
  return PHRASE_CATEGORY_META.reduce(
    (sum, c) => sum + (Array.isArray(bank[c.id]) ? bank[c.id].length : 0),
    0,
  );
};

const psychTriggerList = (triggers) => {
  if (!triggers || typeof triggers !== "object") return [];
  return PSYCH_TRIGGER_META
    .map((m) => ({ ...m, data: triggers[m.id] }))
    .filter((x) => x.data && Number(x.data.count) > 0)
    .sort((a, b) => (b.data.count || 0) - (a.data.count || 0));
};

// =============================================================================
// Lean building blocks (vp- prefix)
// =============================================================================

// A grouped "spec sheet": small uppercase title + hairline-separated rows.
const Group = ({ title, caption, children, wide = false }) => (
  <section className={`vp-group ${wide ? "vp-group--wide" : ""}`}>
    <div className="vp-group-head">
      <h3 className="vp-group-title">{title}</h3>
      {caption && <span className="vp-group-caption">{caption}</span>}
    </div>
    <div className="vp-rows">{children}</div>
  </section>
);

// Single label/value row. `stacked` puts the value on its own line below.
const Row = ({ label, children, stacked = false }) => (
  <div className={`vp-row ${stacked ? "is-stacked" : ""}`}>
    <span className="vp-row-label">{label}</span>
    <div className="vp-row-value">{children}</div>
  </div>
);

const Chips = ({ items, mono = (x) => x }) => {
  const list = (items || []).map(mono).filter(Boolean);
  if (!list.length) return null;
  return (
    <div className="vp-chips">
      {list.map((c, i) => (
        <span key={i} className="vp-chip">{c}</span>
      ))}
    </div>
  );
};

const Quote = ({ children, meta }) => (
  <div className="vp-quote">
    <p className="vp-quote-text">{children}</p>
    {meta && <span className="vp-quote-meta">{meta}</span>}
  </div>
);

// Thin proportional bar (register 1–10, length distribution, etc.)
const Meter = ({ pct, caption }) => (
  <div className="vp-meter">
    <div className="vp-meter-track">
      <div className="vp-meter-fill" style={{ width: `${Math.max(2, Math.min(100, pct))}%` }} />
    </div>
    {caption && <span className="vp-meter-cap">{caption}</span>}
  </div>
);

// Confidence ring for the header — quiet brand-blue arc with the score inside.
const Ring = ({ score = 0, size = 58, stroke = 5 }) => {
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const off = c * (1 - Math.max(0, Math.min(100, score)) / 100);
  return (
    <div className="vp-ring" style={{ width: size, height: size }}>
      <svg width={size} height={size}>
        <circle className="vp-ring-track" cx={size / 2} cy={size / 2} r={r} strokeWidth={stroke} fill="none" />
        <circle
          className="vp-ring-arc"
          cx={size / 2}
          cy={size / 2}
          r={r}
          strokeWidth={stroke}
          fill="none"
          strokeDasharray={c}
          strokeDashoffset={off}
          strokeLinecap="round"
          transform={`rotate(-90 ${size / 2} ${size / 2})`}
        />
      </svg>
      <span className="vp-ring-val">{score}</span>
    </div>
  );
};

// =============================================================================
// Sections
// =============================================================================

const IdentitySection = ({ dna }) => {
  const c = dna.character || {};
  const p = c.punctuationHabits || {};
  const punct = [
    p.capitalization && ["Capitalization", p.capitalization],
    p.dashStyle && ["Dashes", p.dashStyle],
    p.parentheses && ["Parentheses", p.parentheses],
    p.ellipsis && ["Ellipsis", p.ellipsis],
  ].filter(Boolean);

  return (
    <>
      {(c.greeting?.word || c.signOff?.word) && (
        <Group title="Signature" caption="Locked verbatim in every email">
          {c.greeting?.word && (
            <Row label="Greeting">
              <span className="vp-bigword">"{c.greeting.word}"</span>
              {c.greeting.count > 0 && c.greeting.total > 0 && (
                <span className="vp-faint">{c.greeting.count}/{c.greeting.total} emails</span>
              )}
            </Row>
          )}
          {c.signOff?.word && (
            <Row label="Sign-off">
              <span className="vp-bigword">"{c.signOff.word}"</span>
              {c.signOff.count > 0 && c.signOff.total > 0 && (
                <span className="vp-faint">{c.signOff.count}/{c.signOff.total} emails</span>
              )}
            </Row>
          )}
        </Group>
      )}

      {c.characterSummary && (
        <Group title="Portrait">
          <p className="vp-prose">{c.characterSummary}</p>
        </Group>
      )}

      {c.catchphrases?.length > 0 && (
        <Group title="Catchphrases" caption="Weighted by real frequency">
          {c.catchphrases.map((cp, i) => (
            <Quote
              key={i}
              meta={cp.count && cp.total ? `${cp.count}/${cp.total} emails` : cp.frequency}
            >
              "{cp.phrase}"
            </Quote>
          ))}
        </Group>
      )}

      {(c.fillerHabits?.length > 0 || punct.length > 0) && (
        <Group title="Habits">
          {c.fillerHabits?.length > 0 && (
            <Row label="Fillers" stacked>
              <Chips items={c.fillerHabits} />
            </Row>
          )}
          {punct.map(([k, v]) => (
            <Row key={k} label={k}>{v}</Row>
          ))}
        </Group>
      )}
    </>
  );
};

const VoiceSection = ({ dna }) => {
  const r = dna.rhythm || {};
  const sp = dna.structureProfile || {};
  const reg = dna.vocabulary?.registerScale;
  const dist = sp.lengthDistribution || {};
  const distTotal = [1, 2, 3, 4].reduce((s, k) => s + (dist[k] || 0), 0);

  return (
    <>
      <Group title="Rhythm">
        {r.avgSentenceWords != null && (
          <Row label="Words / sentence"><span className="vp-num">{r.avgSentenceWords}</span></Row>
        )}
        {r.sentenceVariance && <Row label="Variance">{r.sentenceVariance}</Row>}
        {r.variancePattern && <Row label="Pattern" stacked><p className="vp-prose">{r.variancePattern}</p></Row>}
        {r.paragraphStyle && <Row label="Paragraphs" stacked><p className="vp-prose">{r.paragraphStyle}</p></Row>}
      </Group>

      {reg != null && (
        <Group title="Register" caption="Casual → formal">
          <Row label={`${reg} / 10`} stacked>
            <Meter pct={(reg / 10) * 100} caption={reg >= 7 ? "leans formal" : reg <= 4 ? "leans casual" : "balanced"} />
          </Row>
        </Group>
      )}

      {sp.avgWordsPerEmail && (
        <Group title="Structure" caption="Typical email shape">
          <Row label="Words / email"><span className="vp-num">{sp.avgWordsPerEmail}</span></Row>
          {sp.avgSentencesPerEmail && (
            <Row label="Sentences / email"><span className="vp-num">{sp.avgSentencesPerEmail}</span></Row>
          )}
          {sp.wordsRange?.max > 0 && (
            <Row label="Range">{sp.wordsRange.min}–{sp.wordsRange.max} words</Row>
          )}
          {distTotal > 0 && (
            <Row label="Sentence length" stacked>
              <div className="vp-dist">
                {[1, 2, 3, 4].map((k) => {
                  const n = dist[k] || 0;
                  if (!n) return null;
                  const pct = Math.round((n / distTotal) * 100);
                  return (
                    <div key={k} className="vp-dist-row">
                      <span className="vp-dist-label">{LENGTH_CLASS_LABEL[k]}</span>
                      <Meter pct={pct} />
                      <span className="vp-dist-pct">{pct}%</span>
                    </div>
                  );
                })}
              </div>
            </Row>
          )}
        </Group>
      )}
    </>
  );
};

const WordsSection = ({ dna }) => {
  const wd = dna.wordDna || {};
  const base = Array.isArray(wd.baseWords) ? wd.baseWords : [];
  const special = Array.isArray(wd.specialWords) ? wd.specialWords : [];
  const legacy = !special.length ? dna.vocabulary?.signatureWords || [] : [];

  return (
    <>
      {(wd.toneRead || base.length || special.length || legacy.length) && (
        <Group title="Word DNA" caption="Lexical fingerprint">
          {wd.toneRead && <Row label="Register read" stacked><p className="vp-prose">{wd.toneRead}</p></Row>}
          {base.length > 0 && (
            <Row label="Everyday" stacked><Chips items={base} mono={wordOf} /></Row>
          )}
          {(special.length > 0 || legacy.length > 0) && (
            <Row label="Character" stacked>
              <Chips items={special.length ? special : legacy} mono={wordOf} />
            </Row>
          )}
        </Group>
      )}

      {(dna.metaphorBank?.length > 0 || dna.vocabulary?.germanPronouns || dna.vocabulary?.loanwords) && (
        <Group title="Texture">
          {dna.metaphorBank?.length > 0 && (
            <Row label="Metaphors" stacked><Chips items={dna.metaphorBank} /></Row>
          )}
          {dna.vocabulary?.germanPronouns && dna.vocabulary.germanPronouns !== "n/a" && (
            <Row label="Pronouns">{dna.vocabulary.germanPronouns}</Row>
          )}
          {dna.vocabulary?.loanwords && (
            <Row label="Loanwords" stacked><p className="vp-prose">{dna.vocabulary.loanwords}</p></Row>
          )}
        </Group>
      )}
    </>
  );
};

const PersuasionSection = ({ dna }) => {
  const triggers = psychTriggerList(dna.psychTriggers);
  const opens = (dna.openingPatterns || []).filter((x) => x?.pattern);
  const closes = (dna.closingPatterns || []).filter((x) => x?.pattern);

  return (
    <>
      {triggers.length > 0 && (
        <Group title="Persuasion techniques" caption="Reproduced, adapted per recipient">
          {triggers.map((t) => (
            <div key={t.id} className="vp-trigger">
              <div className="vp-trigger-head">
                <span className="vp-trigger-name">{t.label}</span>
                <span className="vp-faint">{t.data.count}×</span>
              </div>
              <p className="vp-faint vp-trigger-blurb">{t.blurb}</p>
              {Array.isArray(t.data.examples) && t.data.examples.length > 0 && (
                <p className="vp-quote-text">"{t.data.examples[0]}"</p>
              )}
            </div>
          ))}
        </Group>
      )}

      {(dna.argumentStyle || dna.humorType) && (
        <Group title="Approach">
          {dna.argumentStyle && <Row label="Argument flow" stacked><p className="vp-prose">{dna.argumentStyle}</p></Row>}
          {dna.humorType && <Row label="Humor" stacked><p className="vp-prose">{dna.humorType}</p></Row>}
        </Group>
      )}

      {(opens.length > 0 || closes.length > 0) && (
        <Group title="Openings & closings">
          {opens.length > 0 && (
            <Row label="Opens" stacked>
              <div className="vp-patterns">
                {opens.slice(0, 4).map((o, i) => (
                  <div key={i} className="vp-pattern">
                    <span className="vp-pattern-label">{o.pattern}</span>
                    {o.example && <span className="vp-faint">{o.example}</span>}
                  </div>
                ))}
              </div>
            </Row>
          )}
          {closes.length > 0 && (
            <Row label="Closes" stacked>
              <div className="vp-patterns">
                {closes.slice(0, 4).map((o, i) => (
                  <div key={i} className="vp-pattern">
                    <span className="vp-pattern-label">{o.pattern}</span>
                    {o.example && <span className="vp-faint">{o.example}</span>}
                  </div>
                ))}
              </div>
            </Row>
          )}
        </Group>
      )}
    </>
  );
};

const PhraseCategory = ({ label, phrases }) => {
  const [open, setOpen] = useState(false);
  const visible = open ? phrases : phrases.slice(0, 3);
  return (
    <div className="vp-pcat">
      <div className="vp-pcat-head">
        <span className="vp-pcat-name">{label}</span>
        <span className="vp-faint">{phrases.length}</span>
      </div>
      <div className="vp-pcat-list">
        {visible.map((p, i) => (
          <div key={i} className="vp-pphrase">
            <p className="vp-quote-text">{renderSlotted(p.text)}</p>
            <span className="vp-pphrase-meta">
              {p.subCategory && <span>{prettifySub(p.subCategory)}</span>}
              {LENGTH_CLASS_LABEL[p.lengthClass] && <span>{LENGTH_CLASS_LABEL[p.lengthClass]}</span>}
              {p.count > 1 && <span>×{p.count}</span>}
            </span>
          </div>
        ))}
      </div>
      {phrases.length > 3 && (
        <button type="button" className="vp-textbtn" onClick={() => setOpen((x) => !x)}>
          {open ? "Show fewer" : `Show all ${phrases.length}`}
        </button>
      )}
    </div>
  );
};

const PhrasesSection = ({ dna }) => {
  const bank = dna.phraseBank || {};
  return (
    <Group
      title="Phrase library"
      caption="Real sentences, anonymized as style references"
      wide
    >
      {PHRASE_CATEGORY_META.map((meta) => {
        const phrases = bank[meta.id] || [];
        if (!phrases.length) return null;
        return <PhraseCategory key={meta.id} label={meta.label} phrases={phrases} />;
      })}
    </Group>
  );
};

const LimitsSection = ({ dna }) => (
  <>
    {dna.detectedTaboos?.length > 0 && (
      <Group title="Avoids" caption="The CopyTwin will never do these">
        <Row label="Taboos" stacked><Chips items={dna.detectedTaboos} /></Row>
      </Group>
    )}
    {dna.quirks?.length > 0 && (
      <Group title="Quirks">
        <Row label="Idiosyncrasies" stacked><Chips items={dna.quirks} /></Row>
      </Group>
    )}
    {dna.confidenceNotes && (
      <Group title="Distillation notes">
        <p className="vp-prose vp-faint">{dna.confidenceNotes}</p>
      </Group>
    )}
  </>
);

// =============================================================================
// MAIN
// =============================================================================
const SECTIONS = [
  { id: "identity",   label: "Identity" },
  { id: "voice",      label: "Voice" },
  { id: "words",      label: "Words" },
  { id: "persuasion", label: "Persuasion" },
  { id: "phrases",    label: "Phrases" },
  { id: "limits",     label: "Limits" },
];

const VoiceDnaPreview = ({
  dna,
  cloneName,
  confidenceScore,
  sampleCount = 0,
  minSamples = 2,
  onRetrain,
  isTraining,
}) => {
  const ageLabel = useAgeLabel(dna?.distilledAt);
  const [section, setSection] = useState("identity");
  const [essenceOpen, setEssenceOpen] = useState(false);

  // Empty state — clean, centered, no chrome noise.
  if (!dna) {
    const remaining = Math.max(0, minSamples - sampleCount);
    return (
      <div className="vp vp-empty">
        <div className="vp-empty-mark"><i className="fa-solid fa-fingerprint" /></div>
        <h2 className="vp-empty-title">No voice profile yet</h2>
        <p className="vp-empty-copy">
          {remaining > 0
            ? `Add ${remaining} more sample${remaining === 1 ? "" : "s"} in Setup, then train the CopyTwin.`
            : "Head to Setup and train the CopyTwin to distill its voice profile."}
        </p>
      </div>
    );
  }

  const conf = confidenceMeta(confidenceScore || 0);

  const has = {
    identity:   hasCharacterData(dna.character),
    voice:      !!(dna.rhythm?.avgSentenceWords != null || dna.vocabulary?.registerScale != null || dna.structureProfile?.avgWordsPerEmail),
    words:      !!(dna.wordDna?.baseWords?.length || dna.wordDna?.specialWords?.length || dna.wordDna?.toneRead || dna.vocabulary?.signatureWords?.length || dna.metaphorBank?.length || dna.vocabulary?.germanPronouns || dna.vocabulary?.loanwords),
    persuasion: !!(psychTriggerList(dna.psychTriggers).length || dna.argumentStyle || dna.humorType || dna.openingPatterns?.length || dna.closingPatterns?.length),
    phrases:    hasPhraseBankData(dna.phraseBank),
    limits:     !!(dna.detectedTaboos?.length || dna.quirks?.length || dna.confidenceNotes),
  };
  const visible = SECTIONS.filter((s) => has[s.id]);
  const active = has[section] ? section : (visible[0]?.id || "identity");

  const pbTotal = phraseBankTotal(dna.phraseBank);
  const essence = dna.voiceFingerprint;
  const longEssence = essence && essence.length > 220;

  const metrics = [
    dna.rhythm?.avgSentenceWords != null && { v: dna.rhythm.avgSentenceWords, l: "words / sentence" },
    dna.structureProfile?.avgSentencesPerEmail && { v: dna.structureProfile.avgSentencesPerEmail, l: "sentences / email" },
    dna.vocabulary?.registerScale != null && { v: `${dna.vocabulary.registerScale}/10`, l: "register" },
    { v: dna.sampleCount || sampleCount, l: "samples" },
  ].filter(Boolean);

  return (
    <div className="vp">
      {/* header */}
      <header className="vp-head">
        <div className="vp-head-id">
          <div className="vp-mark"><i className="fa-solid fa-fingerprint" /></div>
          <div className="vp-head-text">
            <div className="vp-eyebrow">Voice profile</div>
            <h2 className="vp-name">{cloneName ? `${cloneName}'s voice` : "Voice profile"}</h2>
            <div className="vp-sub">
              {ageLabel && <span>Trained {ageLabel}</span>}
              {ageLabel && <span className="vp-sub-dot" />}
              <span>{sampleCount} sample{sampleCount === 1 ? "" : "s"}</span>
            </div>
          </div>
        </div>

        <div className="vp-head-side">
          {confidenceScore != null && (
            <div className="vp-conf">
              <Ring score={confidenceScore} />
              <div className="vp-conf-meta">
                <span className="vp-conf-label">{conf.label}</span>
                <span className="vp-conf-cap">confidence</span>
              </div>
            </div>
          )}
          {onRetrain && (
            <button type="button" className="vp-retrain" onClick={onRetrain} disabled={isTraining}>
              <i className={`fa-solid ${isTraining ? "fa-circle-notch fa-spin" : "fa-rotate"}`} />
              {isTraining ? "Training" : "Re-train"}
            </button>
          )}
        </div>
      </header>

      {/* essence */}
      {essence && (
        <div className="vp-essence">
          <p className={`vp-essence-text ${longEssence && !essenceOpen ? "is-clamped" : ""}`}>
            {essence}
          </p>
          {longEssence && (
            <button type="button" className="vp-textbtn" onClick={() => setEssenceOpen((x) => !x)}>
              {essenceOpen ? "Less" : "More"}
            </button>
          )}
        </div>
      )}

      {/* key metrics */}
      {metrics.length > 0 && (
        <div className="vp-metrics">
          {metrics.map((m, i) => (
            <div key={i} className="vp-metric">
              <span className="vp-metric-v">{m.v}</span>
              <span className="vp-metric-l">{m.l}</span>
            </div>
          ))}
        </div>
      )}

      {/* section switcher */}
      {visible.length > 1 && (
        <div className="vp-seg" role="tablist">
          {visible.map((s) => (
            <button
              key={s.id}
              type="button"
              role="tab"
              aria-selected={active === s.id}
              className={`vp-seg-btn ${active === s.id ? "is-active" : ""}`}
              onClick={() => setSection(s.id)}
            >
              {s.label}
              {s.id === "phrases" && pbTotal > 0 && <span className="vp-seg-count">{pbTotal}</span>}
            </button>
          ))}
        </div>
      )}

      {/* content */}
      <div className="vp-body">
        {active === "identity"   && <IdentitySection dna={dna} />}
        {active === "voice"      && <VoiceSection dna={dna} />}
        {active === "words"      && <WordsSection dna={dna} />}
        {active === "persuasion" && <PersuasionSection dna={dna} />}
        {active === "phrases"    && <PhrasesSection dna={dna} />}
        {active === "limits"     && <LimitsSection dna={dna} />}
      </div>
    </div>
  );
};

export default VoiceDnaPreview;
